import { useRef, useState } from 'react';
import { uploadDownloadFile } from '../../services/adminService';

// 파일 확장자로 아이콘 레이블 결정
function fileIcon(name) {
  if (!name) return '📎';
  const ext = name.split('.').pop().toLowerCase();
  const map = { pdf: '📄', zip: '🗜', svg: '🎨', fig: '🎨', figma: '🎨', png: '🖼', jpg: '🖼', jpeg: '🖼', mp4: '🎬', mov: '🎬' };
  return map[ext] || '📎';
}

function fileName(url) {
  if (!url) return '';
  try {
    const parts = new URL(url).pathname.split('/');
    // Supabase URL 패턴: /storage/v1/object/public/bucket/timestamp-name.ext
    const raw = parts[parts.length - 1];
    // 타임스탬프 접두사 제거 (1234567890-filename.ext → filename.ext)
    return raw.replace(/^\d+-/, '');
  } catch {
    return url.split('/').pop();
  }
}

export default function AdminFileInput({ value, onChange, label = '파일 업로드' }) {
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setErr('');

    // 진행 시뮬레이션 (Supabase SDK는 progress 콜백 미지원)
    const timer = setInterval(() => setProgress((p) => Math.min(p + 10, 90)), 150);

    try {
      const { url } = await uploadDownloadFile(file);
      onChange(url);
      setProgress(100);
    } catch (error) {
      setErr(error.message);
    } finally {
      clearInterval(timer);
      setUploading(false);
      e.target.value = '';
      setTimeout(() => setProgress(0), 800);
    }
  };

  const displayName = fileName(value);
  const icon = fileIcon(displayName);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>

      {/* URL 직접 입력 */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          className="admin-input"
          style={{ flex: 1 }}
          placeholder="https://... 또는 아래 버튼으로 업로드"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className="admin-btn-upload"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{ whiteSpace: 'nowrap' }}
        >
          {uploading ? '올리는 중…' : `↑ ${label}`}
        </button>
        <input
          ref={fileRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>

      {/* 업로드 진행바 */}
      {uploading && (
        <div style={{ height: 3, background: 'var(--c-n200)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: 'var(--c-hi)',
            transition: 'width 150ms linear',
          }} />
        </div>
      )}

      {/* 오류 */}
      {err && <span style={{ fontSize: '0.75rem', color: '#ef4444' }}>{err}</span>}

      {/* 업로드된 파일 미리보기 */}
      {value && !uploading && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.625rem',
          padding: '0.5rem 0.75rem',
          background: 'var(--c-n50)',
          border: '1px solid var(--c-n200)',
          borderRadius: 6,
        }}>
          <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 500, color: 'var(--c-n800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName || value}
            </p>
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '0.75rem', color: 'var(--c-hi)', textDecoration: 'none' }}
            >
              링크 확인 ↗
            </a>
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-n400)', fontSize: '1rem', padding: '0 0.25rem', lineHeight: 1 }}
            title="파일 제거"
          >×</button>
        </div>
      )}
    </div>
  );
}
